import type { PortfolioAdminDetail, PortfolioSaveInput } from '@repo/api-contracts';
import { Button } from '@/shared/ui';

export const PortfolioLatestComparison = ({
  original,
  draft,
  draftPreviewUrls,
  draftLogoPreviewUrl,
  latest,
  onReload,
  disabled,
}: {
  original?: PortfolioAdminDetail;
  draft: PortfolioSaveInput;
  draftPreviewUrls: string[];
  draftLogoPreviewUrl: string | null;
  latest: PortfolioAdminDetail;
  onReload: () => void;
  disabled?: boolean;
}) => {
  const rows = [
    { label: '제목', before: original?.title ?? '', draft: draft.title, latest: latest.title },
    {
      label: '고객사명',
      before: original?.clientName ?? '',
      draft: draft.clientName ?? '',
      latest: latest.clientName ?? '',
    },
    {
      label: '회사소개',
      before: original?.companyIntroduction ?? '',
      draft: draft.companyIntroduction,
      latest: latest.companyIntroduction,
    },
    {
      label: '제작배경',
      before: original?.productionBackground ?? '',
      draft: draft.productionBackground,
      latest: latest.productionBackground,
    },
    {
      label: '카테고리',
      before: original?.categories.map(({ name }) => name).join(', ') ?? '',
      draft: draft.categoryIds
        .map(
          (id) =>
            latest.categories.find((category) => category.id === id)?.name ??
            original?.categories.find((category) => category.id === id)?.name ??
            id,
        )
        .join(', '),
      latest: latest.categories.map(({ name }) => name).join(', '),
    },
    {
      label: '상품 순서',
      before: original?.products.map(({ name }) => name).join(', ') ?? '',
      draft: draft.productIds
        .map(
          (id) =>
            latest.products.find((product) => product.id === id)?.name ??
            original?.products.find((product) => product.id === id)?.name ??
            id,
        )
        .join(', '),
      latest: latest.products.map(({ name }) => name).join(', '),
    },
    {
      label: '사진 순서',
      before: original?.images.map(({ id }) => id).join(', ') ?? '',
      draft: draft.images
        .map((image) => (image.kind === 'EXISTING' ? image.imageId : image.assetId))
        .join(', '),
      latest: latest.images.map(({ id }) => id).join(', '),
    },
    {
      label: '로고',
      before: original?.logo ? '등록됨' : '없음',
      draft:
        draft.logo?.kind === 'KEEP' ? '유지' : draft.logo?.kind === 'UPLOAD' ? '새 로고' : '없음',
      latest: latest.logo ? '등록됨' : '없음',
    },
    {
      label: '공개',
      before: original?.isVisible ? '공개' : '비공개',
      draft: draft.isVisible ? '공개' : '비공개',
      latest: latest.isVisible ? '공개' : '비공개',
    },
    {
      label: '홈 선택',
      before: original?.homeSelected ? '선택' : '미선택',
      draft: draft.homeSelected ? '선택' : '미선택',
      latest: latest.homeSelected ? '선택' : '미선택',
    },
  ];
  return (
    <section aria-label="최신 내용 비교" className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <h2 className="font-medium">최신 내용 비교</h2>
      <p className="text-sm">
        확인만으로 작성 내용이나 기준 시각은 바뀌지 않습니다. 최신 내용으로 새로 열면 작성 중인
        내용과 신규 파일을 버립니다.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead>
            <tr>
              {['항목', '편집 시작 내용', '작성 중 내용', '최신 서버 내용'].map((title) => (
                <th key={title} className="border p-2 text-left">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <th className="border p-2 text-left">{row.label}</th>
                {[
                  { column: 'before', value: row.before },
                  { column: 'draft', value: row.draft },
                  { column: 'latest', value: row.latest },
                ].map(({ value, column }) => (
                  <td
                    key={`${row.label}:${column}`}
                    className="whitespace-pre-wrap break-words border p-2"
                  >
                    {value || '없음'}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <th className="border p-2 text-left">사진 미리보기</th>
              {[
                {
                  column: 'before',
                  urls: original?.images.map(({ thumbnailUrl }) => thumbnailUrl) ?? [],
                },
                { column: 'draft', urls: draftPreviewUrls },
                { column: 'latest', urls: latest.images.map(({ thumbnailUrl }) => thumbnailUrl) },
              ].map(({ urls, column }) => (
                <td key={`previews:${column}`} className="border p-2">
                  <div className="flex flex-wrap gap-1">
                    {urls.map((url, index) => (
                      <img
                        key={url}
                        src={url}
                        alt={`비교 사진 ${index + 1}`}
                        className="size-12 object-cover"
                      />
                    ))}
                  </div>
                </td>
              ))}
            </tr>
            <tr>
              <th className="border p-2 text-left">로고 미리보기</th>
              {[
                { column: 'before', url: original?.brandLogoUrl },
                { column: 'draft', url: draftLogoPreviewUrl },
                { column: 'latest', url: latest.brandLogoUrl },
              ].map(({ column, url }) => (
                <td key={`logo:${column}`} className="border p-2">
                  {url ? (
                    <img src={url} alt={`${column} 로고 비교`} className="size-16 object-contain" />
                  ) : (
                    '없음'
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">최신 수정 시각: {latest.updatedAt}</p>
      {original ? (
        <Button type="button" variant="outline" disabled={disabled} onClick={onReload}>
          작성 내용 버리고 최신 내용으로 새로 열기
        </Button>
      ) : null}
    </section>
  );
};
